import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Tooltip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  DataObject as DataObjectIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Cloud as CloudIcon,
  Storage as StorageIcon,
  CloudDone as CloudDoneIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import BreadcrumbsNav from '../components/BreadcrumbsNav';

const ProjectExecutionResults = () => {
  const { companyId, projectId, executionId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const [execution, setExecution] = useState(null);
  const [stepResults, setStepResults] = useState([]);
  const [outputFiles, setOutputFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filePreviewDialog, setFilePreviewDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [dataTableTabIndex, setDataTableTabIndex] = useState(0);
  const [mongoTier, setMongoTier] = useState('M30');
  const [concurrentExecutions, setConcurrentExecutions] = useState(1);

  const fetchExecutionDetails = useCallback(async () => {
    setLoading(!execution); // Only show main loading on first load
    setError('');
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/executions/${executionId}`);
      setExecution(response.data.execution);
      setStepResults(response.data.stepResults || []);
      setOutputFiles(response.data.outputFiles || []);
      
      // Auto-refresh for in-progress executions
      if (response.data.execution.status === 'in_progress' || response.data.execution.status === 'waiting_input') {
        setAutoRefresh(true);
      } else {
        setAutoRefresh(false);
      }
    } catch (err) {
      setError('Failed to fetch execution details');
      console.error('Execution details fetch error:', err);
      setAutoRefresh(false);
    }
    setLoading(false);
  }, [companyId, projectId, executionId, execution]);

  useEffect(() => {
    if (companyId && projectId && executionId) {
      fetchExecutionDetails();
    }
  }, [companyId, projectId, executionId]);

  // Set default tab based on execution status
  useEffect(() => {
    if (execution) {
      // For incomplete or failed executions, default to Steps tab (index 1)
      // For completed executions, default to Results tab (index 0)
      if (execution.status !== 'completed' && activeTab === 0) {
        setActiveTab(1);
      }
    }
  }, [execution, activeTab]);

  // Auto-refresh for in-progress executions
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchExecutionDetails();
      }, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchExecutionDetails]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (selectedFile?.blob && selectedFile?.previewUrl) {
        URL.revokeObjectURL(selectedFile.previewUrl);
      }
    };
  }, [selectedFile]);

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      // Validate parameters
      if (!fileId) {
        console.error('❌ Download failed: fileId is undefined or null');
        alert('Download failed: File ID is missing');
        return;
      }
      
      if (!fileName) {
        console.error('❌ Download failed: fileName is undefined or null');
        alert('Download failed: File name is missing');
        return;
      }
      
      // Try multiple possible endpoints
      // The correct backend route is /api/flows/executions/:executionId/files/:fileId/preview
      const endpoints = [
        // Correct route structure (highest priority)
        `/flows/executions/${executionId}/files/${fileId}/preview`,
        `/flows/executions/${executionId}/files/${fileId}/download`,
        // Alternative route structures
        `/api/flows/executions/${executionId}/files/${fileId}/preview`,
        `/api/flows/executions/${executionId}/files/${fileId}/download`,
        // Company/project specific routes (if they exist)
        `/api/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileId}/preview`,
        `/api/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileId}/download`
      ];
      
      let response;
      let successfulEndpoint = '';
      
      // Try each endpoint until one works
      for (let i = 0; i < endpoints.length; i++) {
        try {
          response = await api.get(endpoints[i], { responseType: 'blob' });
          successfulEndpoint = endpoints[i];
          break;
        } catch (error) {
          if (i === endpoints.length - 1) {
            // All endpoints failed
            throw new Error(`All download endpoints failed. Last error: ${error.response?.status || 'Unknown'}`);
          }
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Download failed:', error);
      alert(`Failed to download file: ${error.message || 'Unknown error'}. Please check file permissions.`);
    }
  };

  // Helper function to check if file is an image
  const isImageFile = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  // Helper function to check if file is a PDF
  const isPdfFile = (fileName) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return extension === '.pdf';
  };

  // Helper function to check if file can be previewed
  const isPreviewableFile = (fileName) => {
    return isImageFile(fileName) || isPdfFile(fileName);
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file click to show preview
  const handleFileClick = (file) => {
    // For input files, use filename as the identifier (this is what the backend expects)
    // For output files, use fileId
    const fileIdentifier = file.filename || file.fileId;
    
    if (!fileIdentifier) {
      console.error('No file identifier found:', file);
      alert('Unable to preview file: file identifier missing');
      return;
    }
    
    // For previewable files (images and PDFs), we need to use the download endpoint to get the decrypted file
    // The download endpoint will handle S3 decryption and return the file as a blob
    if (isPreviewableFile(file.originalName || file.fileName)) {
      // First, open the dialog with loading state
      const selectedFileData = {
        ...file,
        previewUrl: null, // Will be set after download
        isImage: isImageFile(file.originalName || file.fileName),
        isPdf: isPdfFile(file.originalName || file.fileName),
        isPreviewable: isPreviewableFile(file.originalName || file.fileName),
        isLoading: true,
        // Ensure we have the right file identifier for download
        filename: file.filename,
        fileId: file.fileId,
        fileName: file.fileName,
        originalName: file.originalName
      };
      
      setSelectedFile(selectedFileData);
      setFilePreviewDialog(true);
      
      // Then fetch the decrypted file - try multiple possible endpoints
      // The correct backend route is /api/flows/executions/:executionId/files/:fileId/preview
      const endpoints = [
        // Correct route structure (highest priority)
        `/flows/executions/${executionId}/files/${fileIdentifier}/preview`,
        `/flows/executions/${executionId}/files/${fileIdentifier}/download`,
        // Alternative route structures
        `/api/flows/executions/${executionId}/files/${fileIdentifier}/preview`,
        `/api/flows/executions/${executionId}/files/${fileIdentifier}/download`,
        // Company/project specific routes (if they exist)
        `/api/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileIdentifier}/preview`,
        `/api/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileIdentifier}/download`
      ];
      
      // Try each endpoint until one works
      let endpointIndex = 0;
      const tryEndpoint = () => {
        if (endpointIndex >= endpoints.length) {
          // All endpoints failed, try direct fetch to bypass API base URL issue
          
          // Try the correct route structure first using proper API configuration
          const correctUrl = `${api.defaults.baseURL}/flows/executions/${executionId}/files/${fileIdentifier}/preview`;
          
          fetch(correctUrl, {
            method: 'GET',
            headers: {
              'Accept': 'image/*,application/octet-stream'
            }
          }).then(response => {
            if (response.ok) {
              return response.blob();
            } else {
              throw new Error(`Correct route direct fetch failed: ${response.status}`);
            }
          }).then(blob => {
            const previewUrl = URL.createObjectURL(blob);
            setSelectedFile(prev => ({
              ...prev,
              previewUrl,
              isLoading: false,
              blob: blob,
              // Ensure we preserve the original file properties
              filename: prev.filename,
              fileId: prev.fileId,
              fileName: prev.fileName,
              originalName: prev.originalName
            }));
          }).catch(correctError => {
            console.error('❌ Correct route direct fetch failed:', correctError);
            
            // Try the company/project structure as fallback using proper API configuration
            const companyProjectUrl = `${api.defaults.baseURL}/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileIdentifier}/preview`;
            
            fetch(companyProjectUrl, {
              method: 'GET',
              headers: {
                'Accept': 'image/*,application/octet-stream'
              }
            }).then(response => {
              if (response.ok) {
                return response.blob();
              } else {
                throw new Error(`Company/project direct fetch failed: ${response.status}`);
              }
            }).then(blob => {
              const previewUrl = URL.createObjectURL(blob);
              setSelectedFile(prev => ({
                ...prev,
                previewUrl,
                isLoading: false,
                blob: blob,
                // Ensure we preserve the original file properties
                filename: prev.filename,
                fileId: prev.fileId,
                fileName: prev.fileName,
                originalName: prev.originalName
              }));
            }).catch(companyError => {
              console.error('❌ Company/project direct fetch also failed:', companyError);
              setSelectedFile(prev => ({
                ...prev,
                isLoading: false,
                error: `All endpoints failed. Correct route: ${correctError.message}, Company/project: ${companyError.message}. Please check file permissions and API configuration.`,
                // Ensure we preserve the original file properties
                filename: prev.filename,
                fileId: prev.fileId,
                fileName: prev.fileName,
                originalName: prev.originalName
              }));
            });
          });
          return;
        }
        
        const currentEndpoint = endpoints[endpointIndex];
        
        api.get(currentEndpoint, {
          responseType: 'blob',
          headers: {
            'Accept': 'image/*,application/octet-stream'
          }
        }).then(response => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
          const previewUrl = URL.createObjectURL(blob);
          
          setSelectedFile(prev => ({
            ...prev,
            previewUrl,
            isLoading: false,
            blob: blob,
            // Ensure we preserve the original file properties
            filename: prev.filename,
            fileId: prev.fileId,
            fileName: prev.fileName,
            originalName: prev.originalName
          }));
        }).catch(error => {
          console.error(`❌ Endpoint ${endpointIndex + 1} failed:`, currentEndpoint, {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
          });
          endpointIndex++;
          tryEndpoint(); // Try next endpoint
        });
      };
      
      tryEndpoint();
    } else {
      // For non-previewable files, just show the file info
      const selectedFileData = {
        ...file,
        previewUrl: null,
        isImage: false,
        isPdf: false,
        isPreviewable: false,
        isLoading: false,
        // Ensure we have the right file identifier for download
        filename: file.filename,
        fileId: file.fileId,
        fileName: file.fileName,
        originalName: file.originalName
      };
      
      setSelectedFile(selectedFileData);
      setFilePreviewDialog(true);
    }
  };

  // Helper function to get column width styling
  const getColumnWidth = (width) => {
    if (!width || width === 'auto') return undefined;
    
    // Handle preset widths
    switch (width) {
      case 'narrow': return '100px';
      case 'medium': return '150px';
      case 'wide': return '200px';
      default: return width; // Use custom values like '120px', '20%', etc.
    }
  };

  // Helper function to format cell value based on column type
  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (column.type) {
      case 'currency':
        // Handle currency formatting
        if (typeof value === 'string' && /^[A-Za-z$€£¥₹₽]+/.test(value)) {
          return value; // Already formatted
        }
        
        let numValue = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
        if (isNaN(numValue)) return String(value);
        
        const currencySymbol = column.currencySymbol || '$';
        const thousandsSeparator = column.thousandsSeparator || ',';
        const decimalSeparator = column.decimalSeparator || '.';
        const decimalPlaces = column.decimalPlaces !== undefined ? column.decimalPlaces : 2;
        
        const formattedNumber = numValue.toFixed(decimalPlaces);
        const [integerPart, decimalPart] = formattedNumber.split('.');
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
        
        if (decimalPlaces > 0 && decimalPart) {
          return `${currencySymbol}${formattedInteger}${decimalSeparator}${decimalPart}`;
        } else {
          return `${currencySymbol}${formattedInteger}`;
        }
        
      case 'number':
        // Handle both numeric values and string values with intelligent parsing
        let num;
        if (typeof value === 'number') {
          num = value;
        } else {
          // Use column-specific separators for intelligent parsing
          const numberThousandsSeparator = column.thousandsSeparator || ',';
          const numberDecimalSeparator = column.decimalSeparator || '.';
          let stringValue = String(value);
          
          // Intelligent parsing based on column configuration
          if (stringValue.includes(numberThousandsSeparator) && stringValue.includes(numberDecimalSeparator) && numberThousandsSeparator !== numberDecimalSeparator) {
            // Both separators present and different - parse accordingly
            const lastDecimalIndex = stringValue.lastIndexOf(numberDecimalSeparator);
            const integerPart = stringValue.substring(0, lastDecimalIndex).replace(new RegExp('\\' + numberThousandsSeparator, 'g'), '');
            const decimalPart = stringValue.substring(lastDecimalIndex + 1);
            num = parseFloat(`${integerPart}.${decimalPart}`);
          } else if (stringValue.includes(numberThousandsSeparator) && numberThousandsSeparator !== '.') {
            // Only thousands separators (and they're not dots)
            num = parseFloat(stringValue.replace(new RegExp('\\' + numberThousandsSeparator, 'g'), ''));
          } else if (stringValue.includes('.') && numberThousandsSeparator === '.' && numberDecimalSeparator !== '.') {
            // Dots are thousands separators, not decimal points
            num = parseFloat(stringValue.replace(/\./g, ''));
          } else if (stringValue.includes(numberDecimalSeparator) && numberDecimalSeparator !== '.') {
            // Custom decimal separator (not dot)
            num = parseFloat(stringValue.replace(numberDecimalSeparator, '.'));
          } else {
            // Default parsing - handle as standard number
            let cleanNumber = stringValue.replace(/[^0-9.-]/g, ''); // Remove all non-numeric except decimal point and minus
            num = parseFloat(cleanNumber);
          }
        }
        
        if (isNaN(num)) return String(value);
        
        const numberDecimalPlaces = column.decimalPlaces !== undefined ? column.decimalPlaces : 0;
        const numberThousandsSeparator = column.thousandsSeparator || ',';
        const numberDecimalSeparator = column.decimalSeparator || '.';
        
        const formattedNum = num.toFixed(numberDecimalPlaces);
        const [numIntegerPart, numDecimalPart] = formattedNum.split('.');
        const formattedNumInteger = numIntegerPart.replace(/\B(?=(\d{3})+(?!\d))/g, numberThousandsSeparator);
        
        if (numberDecimalPlaces > 0 && numDecimalPart) {
          return `${formattedNumInteger}${numberDecimalSeparator}${numDecimalPart}`;
        } else {
          return formattedNumInteger;
        }
        
      case 'date':
        try {
          if (!value) return '-';
          
          let dateValue;
          if (value instanceof Date) {
            dateValue = value;
          } else {
            const dateStr = String(value).trim();
            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              dateValue = new Date(dateStr);
            } else if (dateStr.match(/^\d{2}[./-]\d{2}[./-]\d{4}$/)) {
              const parts = dateStr.split(/[./-]/);
              dateValue = new Date(parts[2], parts[1] - 1, parts[0]);
            } else {
              dateValue = new Date(dateStr);
            }
          }
          
          if (isNaN(dateValue.getTime())) return String(value);
          
          const dateFormat = column.dateFormat || 'MM/dd/yyyy';
          const day = dateValue.getDate().toString().padStart(2, '0');
          const month = (dateValue.getMonth() + 1).toString().padStart(2, '0');
          const year = dateValue.getFullYear();
          
          switch (dateFormat) {
            case 'dd.MM.yyyy': return `${day}.${month}.${year}`;
            case 'dd/MM/yyyy': return `${day}/${month}/${year}`;
            case 'MM/dd/yyyy': return `${month}/${day}/${year}`;
            case 'yyyy-MM-dd': return `${year}-${month}-${day}`;
            default: return `${month}/${day}/${year}`;
          }
        } catch (error) {
          return String(value);
        }
        
      case 'boolean':
        return value ? 'Yes' : 'No';
      default:
        return String(value);
    }
  };

  const getStepIcon = (stepType, status) => {
    const iconProps = { fontSize: 'small' };
    
    if (status === 'failed') return <ErrorIcon color="error" {...iconProps} />;
    if (status === 'completed') return <CheckCircleIcon color="success" {...iconProps} />;
    if (status === 'in_progress') return <ScheduleIcon color="warning" {...iconProps} />;
    
    switch (stepType) {
      case 'file_upload':
      case 'file_input':
        return <AssignmentIcon {...iconProps} />;
      case 'ai_extraction':
      case 'data_extraction':
        return <DataObjectIcon {...iconProps} />;
      case 'api_call':
      case 'enhanced_api_call':
        return <CodeIcon {...iconProps} />;
      case 'data_transformation':
      case 'variable_operations':
        return <AssessmentIcon {...iconProps} />;
      case 'end':
        return <CheckCircleIcon {...iconProps} />;
      default:
        return <PlayArrowIcon {...iconProps} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'in_progress': return 'warning';
      case 'waiting_input': return 'info';
      default: return 'default';
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // --- Cost Estimation Logic ---

  const LLM_PRICING_PER_MILLION_TOKENS = {
    'gpt-4o':                          { input: 2.50,  output: 10.00, label: 'GPT-4o' },
    'gpt-4o-mini':                     { input: 0.15,  output: 0.60,  label: 'GPT-4o Mini' },
    'gpt-3.5-turbo':                   { input: 0.50,  output: 1.50,  label: 'GPT-3.5 Turbo' },
    'gpt-5':                           { input: 5.00,  output: 20.00, label: 'GPT-5' },
    'gpt-5-mini':                      { input: 1.25,  output: 5.00,  label: 'GPT-5 Mini' },
    'gpt-5-nano':                      { input: 0.25,  output: 1.00,  label: 'GPT-5 Nano' },
    'gpt-4.1':                         { input: 2.00,  output: 8.00,  label: 'GPT-4.1' },
    'gpt-4.1-mini':                    { input: 0.40,  output: 1.60,  label: 'GPT-4.1 Mini' },
    'gpt-4.1-nano':                    { input: 0.10,  output: 0.40,  label: 'GPT-4.1 Nano' },
    'bedrock-claude-3-haiku':          { input: 0.25,  output: 1.25,  label: 'Claude 3 Haiku (Bedrock)' },
    'bedrock-claude-3-sonnet':         { input: 3.00,  output: 15.00, label: 'Claude 3 Sonnet (Bedrock)' },
    'bedrock-claude-3-5-sonnet':       { input: 3.00,  output: 15.00, label: 'Claude 3.5 Sonnet (Bedrock)' },
    'bedrock-claude-3-5-sonnet-v2':    { input: 3.00,  output: 15.00, label: 'Claude 3.5 Sonnet v2 (Bedrock)' },
    'bedrock-claude-3-7-sonnet':       { input: 3.00,  output: 15.00, label: 'Claude 3.7 Sonnet (Bedrock)' },
    'bedrock-claude-sonnet-4':         { input: 3.00,  output: 15.00, label: 'Claude Sonnet 4 (Bedrock)' },
    'anthropic-claude-sonnet-4':       { input: 3.00,  output: 15.00, label: 'Claude Sonnet 4' },
    'anthropic-claude-opus-4':         { input: 15.00, output: 75.00, label: 'Claude Opus 4' },
    'gemini-2.5-flash':                { input: 0.15,  output: 0.60,  label: 'Gemini 2.5 Flash' },
    'gemini-2.0-flash':                { input: 0.10,  output: 0.40,  label: 'Gemini 2.0 Flash' },
    'gemini-1.5-flash':                { input: 0.075, output: 0.30,  label: 'Gemini 1.5 Flash' },
    'gemini-1.5-pro':                  { input: 1.25,  output: 5.00,  label: 'Gemini 1.5 Pro' },
    'qwen-plus':                       { input: 0.80,  output: 2.00,  label: 'Qwen Plus' },
    'qwen-max':                        { input: 2.40,  output: 9.60,  label: 'Qwen Max' },
    'qwen-vl-plus':                    { input: 1.00,  output: 3.00,  label: 'Qwen VL Plus' },
  };

  // AWS EC2 t3.medium on-demand price in ap-southeast-1 (Singapore)
  const EC2_T3_MEDIUM_HOURLY = 0.0520;

  // MongoDB Atlas hourly rates for AWS ap-southeast-1 (Singapore), approximate on-demand pricing
  const MONGODB_ATLAS_TIERS = {
    M10:  { hourlyRate: 0.09,  ram: '2 GB',  storage: '10 GB',  vcpu: '2 vCPUs (shared)' },
    M20:  { hourlyRate: 0.22,  ram: '4 GB',  storage: '20 GB',  vcpu: '2 vCPUs (shared)' },
    M30:  { hourlyRate: 0.59,  ram: '8 GB',  storage: '40 GB',  vcpu: '2 vCPUs' },
    M40:  { hourlyRate: 1.18,  ram: '16 GB', storage: '80 GB',  vcpu: '4 vCPUs' },
    M50:  { hourlyRate: 2.22,  ram: '32 GB', storage: '160 GB', vcpu: '8 vCPUs' },
    M60:  { hourlyRate: 4.30,  ram: '64 GB', storage: '320 GB', vcpu: '16 vCPUs' },
  };

  // AWS Textract pricing in ap-southeast-1 (Singapore), per page
  // DetectDocumentText: $1.50/1000 pages
  // AnalyzeDocument (Forms or Tables): $15.00/1000 pages
  // AnalyzeDocument (Forms + Tables): $15.00/1000 pages (same tier, both features)
  // AnalyzeExpense: $21.00/1000 pages
  const TEXTRACT_PRICING = {
    detect_text:       { perPage: 0.0015, label: 'DetectDocumentText' },
    analyze_forms:     { perPage: 0.0150, label: 'AnalyzeDocument (Forms)' },
    analyze_tables:    { perPage: 0.0150, label: 'AnalyzeDocument (Tables)' },
    analyze_forms_tables: { perPage: 0.0150, label: 'AnalyzeDocument (Forms + Tables)' },
    analyze_expense:   { perPage: 0.0210, label: 'AnalyzeExpense' },
  };

  // AWS S3 pricing in ap-southeast-1 (Singapore)
  const S3_PRICING = {
    storagePerGBMonth: 0.025,   // Standard storage per GB/month
    putPer1000:        0.0055,  // PUT, COPY, POST per 1,000 requests
    getPer1000:        0.00044, // GET, SELECT per 1,000 requests
    dataTransferOutPerGB: 0.12, // Data transfer out to internet per GB
  };

  // AWS SQS pricing (Standard Queue)
  const SQS_PRICE_PER_MILLION = 0.40; // per 1M requests (first 1M/month free)

  // AWS Bedrock on-demand infrastructure overhead per 1,000 input tokens (ap-southeast-1)
  // This is in addition to the model token cost — accounts for cross-region routing + inference overhead
  const BEDROCK_OVERHEAD_PER_1K_INPUT_TOKENS = 0.0002;

  // MongoDB Atlas data transfer pricing (within AWS region ap-southeast-1)
  const MONGO_DATA_TRANSFER_PER_GB = 0.015;
  // Estimated DB read/write bytes per execution step (rough average)
  const MONGO_BYTES_PER_STEP_ESTIMATE = 0.05; // ~50KB per step in + out

  const calculateCostEstimate = (tier = mongoTier, concurrent = concurrentExecutions) => {
    if (!execution || !stepResults) return null;

    const safeConurrent = Math.max(1, concurrent || 1);
    const tierInfo = MONGODB_ATLAS_TIERS[tier] || MONGODB_ATLAS_TIERS.M30;

    const durationMs = execution.actualProcessingTime
      ? execution.actualProcessingTime * 1000
      : execution.duration || execution.metrics?.totalDuration || 0;
    const durationHours = durationMs / (1000 * 60 * 60);

    // --- LLM costs per model ---
    const modelUsage = {};
    for (const step of stepResults) {
      const models = [];
      const tokenCounts = [];

      if (step.aiUsage?.model) {
        models.push(step.aiUsage.model);
        tokenCounts.push(step.aiUsage.tokensUsed || 0);
      }
      if (step.performanceMetrics?.aiModelUsed) {
        models.push(step.performanceMetrics.aiModelUsed);
        tokenCounts.push(step.performanceMetrics.aiTokens || 0);
      }
      if (step.performanceMetrics?.llmModel && !models.includes(step.performanceMetrics.llmModel)) {
        models.push(step.performanceMetrics.llmModel);
        tokenCounts.push(step.performanceMetrics.aiTokens || 0);
      }
      if (step.performanceMetrics?.visionModel && !models.includes(step.performanceMetrics.visionModel)) {
        models.push(step.performanceMetrics.visionModel);
        tokenCounts.push(step.performanceMetrics.aiTokens || 0);
      }

      models.forEach((model, i) => {
        if (!model) return;
        const key = model.toLowerCase().trim();
        if (!modelUsage[key]) {
          modelUsage[key] = { tokens: 0, calls: 0 };
        }
        modelUsage[key].tokens += tokenCounts[i] || 0;
        modelUsage[key].calls += 1;
      });
    }

    let totalLlmCost = 0;
    const llmBreakdown = [];
    let bedrockInputTokensTotal = 0;
    for (const [model, usage] of Object.entries(modelUsage)) {
      const pricing = LLM_PRICING_PER_MILLION_TOKENS[model];
      const avgPerMillion = pricing
        ? (pricing.input + pricing.output) / 2
        : 3.00;
      const cost = (usage.tokens / 1_000_000) * avgPerMillion;
      totalLlmCost += cost;
      llmBreakdown.push({
        model,
        label: pricing?.label || model,
        tokens: usage.tokens,
        calls: usage.calls,
        cost,
        ratePerMillion: avgPerMillion,
      });
      if (model.startsWith('bedrock-')) {
        bedrockInputTokensTotal += usage.tokens;
      }
    }

    // Also factor in metrics.totalTokensUsed if steps didn't capture everything
    const metricTokens = execution.metrics?.totalTokensUsed || 0;
    const stepTotalTokens = Object.values(modelUsage).reduce((sum, u) => sum + u.tokens, 0);
    if (metricTokens > stepTotalTokens && stepTotalTokens > 0) {
      const uncaptured = metricTokens - stepTotalTokens;
      const fallbackCost = (uncaptured / 1_000_000) * 3.00;
      totalLlmCost += fallbackCost;
      llmBreakdown.push({
        model: 'other',
        label: 'Other / Untracked Tokens',
        tokens: uncaptured,
        calls: 0,
        cost: fallbackCost,
        ratePerMillion: 3.00,
      });
    }

    // --- EC2 t3.medium cost (pro-rated, shared across concurrent executions) ---
    const ec2GrossCost = durationHours * EC2_T3_MEDIUM_HOURLY;
    const ec2Cost = ec2GrossCost / safeConurrent;

    // --- MongoDB Atlas cost (pro-rated, shared across concurrent executions) ---
    const mongoGrossCost = durationHours * tierInfo.hourlyRate;
    const mongoOverhead = 0.0001;
    const mongoCost = mongoGrossCost / safeConurrent + mongoOverhead;

    // --- AWS Textract cost ---
    // Derive per-step page counts from pageBreakdown or totalPagesProcessed
    const textractBreakdown = [];
    let totalTextractCost = 0;
    let totalTextractPages = 0;

    for (const step of stepResults) {
      const isTextract = step.performanceMetrics?.awsTextract === true;
      if (!isTextract) continue;

      // Determine which Textract API was used from ocrEngine field
      const engine = (step.performanceMetrics?.ocrEngine || '').toLowerCase();
      let apiKey = 'detect_text'; // default
      if (engine.includes('expense')) {
        apiKey = 'analyze_expense';
      } else if (engine.includes('forms') && engine.includes('tables')) {
        apiKey = 'analyze_forms_tables';
      } else if (engine.includes('forms')) {
        apiKey = 'analyze_forms';
      } else if (engine.includes('tables')) {
        apiKey = 'analyze_tables';
      }

      // Try to get page count from step metrics; fall back to file count as proxy
      const stepPages = step.performanceMetrics?.pageCount
        || step.performanceMetrics?.fileCount
        || 1;

      const apiInfo = TEXTRACT_PRICING[apiKey];
      const stepCost = stepPages * apiInfo.perPage;
      totalTextractCost += stepCost;
      totalTextractPages += stepPages;

      textractBreakdown.push({
        stepName: step.stepName || step.stepId,
        apiType: apiKey,
        label: apiInfo.label,
        pages: stepPages,
        perPage: apiInfo.perPage,
        cost: stepCost,
      });
    }

    // If no step-level Textract data but we know pages were processed, use totalPagesProcessed
    if (textractBreakdown.length === 0 && (execution.metrics?.totalPagesProcessed || 0) > 0) {
      const pages = execution.metrics.totalPagesProcessed;
      const cost = pages * TEXTRACT_PRICING.detect_text.perPage;
      totalTextractCost = cost;
      totalTextractPages = pages;
      textractBreakdown.push({
        stepName: 'All OCR Steps',
        apiType: 'detect_text',
        label: TEXTRACT_PRICING.detect_text.label,
        pages,
        perPage: TEXTRACT_PRICING.detect_text.perPage,
        cost,
        estimated: true,
      });
    }

    // --- AWS SQS cost (Textract job queue) ---
    // Each Textract step sends ~3 SQS messages (enqueue, poll, delete)
    const sqsMessages = textractBreakdown.length * 3;
    const sqsCost = (sqsMessages / 1_000_000) * SQS_PRICE_PER_MILLION;

    // --- AWS S3 cost ---
    // Count total file bytes uploaded across input files
    const inputFilesBytes = (execution?.inputData?.files || [])
      .reduce((sum, f) => sum + (f.fileSize || 0), 0);

    // Count large variables offloaded to S3
    const s3VarsCount = execution?.metrics?.s3VariablesCount || 0;
    const s3VarsBytesEstimate = s3VarsCount * 512 * 1024; // ~512KB per variable estimate

    // Count output files
    const outputFilesBytes = (outputFiles || [])
      .reduce((sum, f) => sum + (f.fileSize || f.size || 0), 0);

    const totalS3Bytes = inputFilesBytes + s3VarsBytesEstimate + outputFilesBytes;
    const totalS3GB = totalS3Bytes / (1024 * 1024 * 1024);

    // Storage cost: monthly rate pro-rated to execution duration
    const durationMonths = durationHours / (24 * 30);
    const s3StorageCost = totalS3GB * S3_PRICING.storagePerGBMonth * durationMonths;

    // Request costs: 1 PUT per file uploaded + 1 GET per file downloaded (preview/export)
    const totalFiles = (execution?.inputData?.files?.length || 0)
      + (outputFiles?.length || 0)
      + s3VarsCount * 2; // upload + download per variable
    const s3RequestCost = (totalFiles / 1000) * (S3_PRICING.putPer1000 + S3_PRICING.getPer1000);

    // Data transfer: output files sent to user
    const s3TransferGB = outputFilesBytes / (1024 * 1024 * 1024);
    const s3TransferCost = s3TransferGB * S3_PRICING.dataTransferOutPerGB;

    const totalS3Cost = s3StorageCost + s3RequestCost + s3TransferCost;

    // --- AWS Bedrock infrastructure overhead ---
    const bedrockOverheadCost = (bedrockInputTokensTotal / 1000) * BEDROCK_OVERHEAD_PER_1K_INPUT_TOKENS;

    // --- MongoDB Atlas data transfer cost ---
    // Estimate based on number of steps (reads + writes per step)
    const stepCount = stepResults.length || 1;
    const mongoTransferGB = (stepCount * MONGO_BYTES_PER_STEP_ESTIMATE) / 1024; // MB → GB
    const mongoTransferCost = mongoTransferGB * MONGO_DATA_TRANSFER_PER_GB;

    const totalCost = totalLlmCost + ec2Cost + mongoCost + totalTextractCost
      + sqsCost + totalS3Cost + bedrockOverheadCost + mongoTransferCost;

    return {
      totalCost,
      durationMs,
      durationHours,
      concurrentExecutions: safeConurrent,
      llm: { total: totalLlmCost, breakdown: llmBreakdown },
      ec2: {
        total: ec2Cost,
        grossCost: ec2GrossCost,
        hourlyRate: EC2_T3_MEDIUM_HOURLY,
      },
      mongo: {
        total: mongoCost,
        grossCost: mongoGrossCost,
        hourlyRate: tierInfo.hourlyRate,
        overhead: mongoOverhead,
        tier,
        tierInfo,
        transfer: { total: mongoTransferCost, gb: mongoTransferGB, stepCount },
      },
      textract: {
        total: totalTextractCost,
        pages: totalTextractPages,
        breakdown: textractBreakdown,
        used: totalTextractPages > 0,
      },
      sqs: {
        total: sqsCost,
        messages: sqsMessages,
        used: sqsMessages > 0,
      },
      s3: {
        total: totalS3Cost,
        storageCost: s3StorageCost,
        requestCost: s3RequestCost,
        transferCost: s3TransferCost,
        totalBytes: totalS3Bytes,
        inputFiles: execution?.inputData?.files?.length || 0,
        outputFiles: outputFiles?.length || 0,
        s3VarsCount,
      },
      bedrock: {
        total: bedrockOverheadCost,
        inputTokens: bedrockInputTokensTotal,
        used: bedrockInputTokensTotal > 0,
      },
    };
  };

  const handleViewStepResult = (result) => {
    setSelectedResult(result);
    setDialogOpen(true);
  };

  const getVariableData = (variableName) => {
    if (!execution?.variables) return null;
    return execution.variables[variableName];
  };

  const resolveTemplateVariables = (text) => {
    if (typeof text !== 'string') {
      return text;
    }
    
    return text.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
      const value = getVariableData(varName);
      return value !== undefined ? String(value) : match;
    });
  };

  // Helper function to check if a value is an S3 reference
  const isS3Reference = (value) => {
    return value && typeof value === 'object' && value._s3Ref === true;
  };

  // Helper function to count S3 references in an object
  const countS3References = (obj) => {
    if (!obj || typeof obj !== 'object') return 0;
    let count = 0;
    
    const checkValue = (val) => {
      if (isS3Reference(val)) {
        count++;
      } else if (val && typeof val === 'object' && !Array.isArray(val)) {
        Object.values(val).forEach(checkValue);
      } else if (Array.isArray(val)) {
        val.forEach(checkValue);
      }
    };
    
    Object.values(obj).forEach(checkValue);
    return count;
  };

  // Helper function to render S3 reference info
  const renderS3ReferenceInfo = (value) => {
    if (!isS3Reference(value)) return null;
    return (
      <Box sx={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: 0.5, 
        backgroundColor: '#e3f2fd', 
        borderRadius: 1, 
        px: 1, 
        py: 0.25 
      }}>
        <CloudIcon sx={{ fontSize: 14, color: '#1565c0' }} />
        <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 500 }}>
          S3 Stored ({((value.originalSize || 0) / (1024 * 1024)).toFixed(2)} MB)
        </Typography>
      </Box>
    );
  };

  // Function to render execution results based on End node configuration
  const renderExecutionResults = () => {
    // Handle different execution states
    if (execution.status === 'failed') {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Execution Failed
          </Typography>
          <Typography variant="body2">
            The workflow execution encountered an error and could not be completed. Please check the execution steps for error details.
          </Typography>
        </Alert>
      );
    }
    
    if (execution.status === 'in_progress' || execution.status === 'waiting_input') {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Execution In Progress
          </Typography>
          <Typography variant="body2">
            The workflow is currently {execution.status === 'waiting_input' ? 'waiting for user input' : 'in progress'}. 
            You can monitor the execution steps to see what has been completed and what's pending.
          </Typography>
        </Alert>
      );
    }
    
    if (execution.status === 'cancelled') {
      return (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Execution Cancelled
          </Typography>
          <Typography variant="body2">
            The workflow execution was cancelled before completion. You can view the completed steps in the Steps tab.
          </Typography>
        </Alert>
      );
    }

    // Find the End node step result for completed executions
    const endStepResult = stepResults.find(result => 
      result.stepType === 'end' && result.status === 'completed'
    );
    
    if (!endStepResult || !endStepResult.output) {
      // Fallback to generic completion message
      return (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Workflow Execution Completed!
          </Typography>
          <Typography variant="body2">
            All steps have been executed successfully. You can view the detailed results in the Steps tab.
          </Typography>
        </Alert>
      );
    }
    
    const result = endStepResult.output;
    const interfaceType = result.interfaceType || 'summary_card';
    const rawResultsTitle = result.resultsTitle || 'Execution Results';
    const resultsTitle = resolveTemplateVariables(rawResultsTitle);
    const formattedOutput = result.formattedOutput;
    const exportConfig = result.exportConfiguration;
    
    console.log('🎨 End step result found:', { 
      hasResult: !!result, 
      interfaceType, 
      rawResultsTitle,
      resultsTitle, 
      hasFormattedOutput: !!formattedOutput,
      hasExportConfig: !!exportConfig,
      resultKeys: Object.keys(result)
    });
    
    return (
      <Card elevation={3} sx={{ border: '2px solid #4caf50', borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {resultsTitle}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Workflow completed successfully
              </Typography>
            </Box>
          </Box>
          
          {formattedOutput ? renderInterfaceOutput(formattedOutput, interfaceType) : renderDefaultOutput(result)}
          
          {exportConfig && exportConfig.enabled && renderExportOptions(exportConfig)}
        </CardContent>
      </Card>
    );
  };

  // Render interface output based on type
  const renderInterfaceOutput = (formattedOutput, interfaceType) => {
    switch (interfaceType) {
      case 'summary_card':
        return renderSummaryCard(formattedOutput);
      case 'data_table':
        return renderDataTable(formattedOutput);
      case 'detailed_report':
        return renderDetailedReport(formattedOutput);
      case 'dashboard':
        return renderDashboard(formattedOutput);
      case 'custom_template':
        return renderCustomTemplate(formattedOutput);
      default:
        return renderDefaultOutput(formattedOutput);
    }
  };

  // Summary Card Interface
  const renderSummaryCard = (output) => {
    const fields = output.fields || [];
    
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Summary
        </Typography>
                 <Grid container spacing={2}>
           {fields.map((field, index) => (
             <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Paper sx={{ p: 2, backgroundColor: field.priority === 'high' ? '#e8f5e8' : '#f5f5f5' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {field.label}
                </Typography>
                <Typography variant="body1">
                  {field.formattedValue || field.value || '-'}
                </Typography>
                {field.priority === 'high' && (
                  <Chip label="Important" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Conditional formatting helpers for data_table
  const dtEvaluateSingleCondition = (variableValue, operator, expectedValue) => {
    switch (operator) {
      case 'equals':
        // eslint-disable-next-line eqeqeq
        return variableValue == expectedValue;
      case 'not_equals':
        // eslint-disable-next-line eqeqeq
        return variableValue != expectedValue;
      case 'contains':
        return String(variableValue).includes(String(expectedValue));
      case 'not_contains':
        return !String(variableValue).includes(String(expectedValue));
      case 'exists':
        return variableValue !== undefined && variableValue !== null && variableValue !== '';
      case 'not_exists':
        return variableValue === undefined || variableValue === null || variableValue === '';
      case 'greater_than':
        return parseFloat(variableValue) > parseFloat(expectedValue);
      case 'less_than':
        return parseFloat(variableValue) < parseFloat(expectedValue);
      case 'is_true':
        return variableValue === true || variableValue === 'true' || variableValue === 1;
      case 'is_false':
        return variableValue === false || variableValue === 'false' || variableValue === 0;
      default:
        return false;
    }
  };

  const dtResolveRowVars = (text, rawRow) => {
    if (typeof text !== 'string') return text;
    return text.replace(/\{\{([\w.]+)\}\}/g, (match, varPath) => {
      if (rawRow && rawRow[varPath] !== undefined) return String(rawRow[varPath]);
      return match;
    });
  };

  const dtEvaluateCondition = (rawRow, condition) => {
    if (!condition || !condition.variable) return false;
    const variableValue = rawRow[condition.variable];
    let resolvedValue = condition.value;
    if (typeof condition.value === 'string' && condition.value.includes('{{')) {
      resolvedValue = dtResolveRowVars(condition.value, rawRow);
    }
    const first = dtEvaluateSingleCondition(variableValue, condition.operator, resolvedValue);
    if (!condition.secondVariable || !condition.secondOperator) return first;
    const secondVarValue = rawRow[condition.secondVariable];
    let resolvedSecond = condition.secondValue;
    if (typeof condition.secondValue === 'string' && condition.secondValue.includes('{{')) {
      resolvedSecond = dtResolveRowVars(condition.secondValue, rawRow);
    }
    const second = dtEvaluateSingleCondition(secondVarValue, condition.secondOperator, resolvedSecond);
    return condition.logicalOperator === 'OR' ? (first || second) : (first && second);
  };

  const dtGetRowColor = (rawRow, rowColorRules) => {
    for (const rule of rowColorRules) {
      if (dtEvaluateCondition(rawRow, rule.condition)) return rule.color;
    }
    return 'transparent';
  };

  const dtGetCellColors = (rawRow, fieldKey, cellColorRules) => {
    const applicable = cellColorRules.filter(r => r.fieldKey === fieldKey);
    for (const rule of applicable) {
      if (dtEvaluateCondition(rawRow, rule.condition)) {
        return { fontColor: rule.color || 'inherit', backgroundColor: rule.backgroundColor || '' };
      }
    }
    return { fontColor: 'inherit', backgroundColor: '' };
  };

  const dtDarkenColor = (hex, amount = 0.15) => {
    if (!hex || hex === 'transparent') return hex;
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = Math.max(0, Math.round(parseInt(hex.substring(0, 2), 16) * (1 - amount)));
    const g = Math.max(0, Math.round(parseInt(hex.substring(2, 4), 16) * (1 - amount)));
    const b = Math.max(0, Math.round(parseInt(hex.substring(4, 6), 16) * (1 - amount)));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // Renders a single data table panel (shared between single-tab and multi-tab modes)
  const renderSingleDataTablePanel = (tabData) => {
    const columns = tabData.columns || [];
    const rows = tabData.rows || [];
    const rawRows = tabData.rawRows || rows;
    const rowColorRules = tabData.rowColorRules || [];
    const cellColorRules = tabData.cellColorRules || [];

    if (tabData.error) {
      return (
        <Alert severity="error">
          <Typography variant="subtitle2">Table Display Error</Typography>
          <Typography variant="body2">{tabData.error}</Typography>
        </Alert>
      );
    }

    if (columns.length === 0) {
      return (
        <Alert severity="warning">
          <Typography variant="subtitle2">No Table Configuration</Typography>
          <Typography variant="body2">No table columns are configured for display.</Typography>
        </Alert>
      );
    }

    if (rows.length === 0) {
      return (
        <Alert severity="info">
          <Typography variant="subtitle2">No Data Available</Typography>
          <Typography variant="body2">No data rows are available to display.</Typography>
        </Alert>
      );
    }

    const headerFields = tabData.headerFields || [];

    return (
      <Box>
        {headerFields.length > 0 && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
              Summary Information
            </Typography>
            <Grid container spacing={2}>
              {headerFields.map((field, index) => {
                const getFieldWidth = (width) => {
                  switch (width) {
                    case 'half': return 6;
                    case 'third': return 4;
                    case 'quarter': return 3;
                    case 'full':
                    default: return 12;
                  }
                };

                return (
                  <Grid item xs={12} sm={getFieldWidth(field.width)} key={field.id || index}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                        {field.label || field.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.primary' }}>
                        {field.defaultValue ? resolveTemplateVariables(field.defaultValue) : (field.value || '-')}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        <TableContainer component={Paper} sx={{ maxHeight: 400, overflowX: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    width: '60px',
                    minWidth: '60px',
                    maxWidth: '60px',
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    left: 0,
                    zIndex: '10 !important',
                    borderRight: '2px solid #e0e0e0'
                  }}
                >
                  #
                </TableCell>
                {columns.map((column, index) => (
                  <TableCell
                    key={`header-${column.key}-${column.id}-${index}`}
                    align={column.alignment || 'left'}
                    sx={{
                      width: getColumnWidth(column.width),
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => {
                const rowId = row.id || row._id || `row-${rowIndex}`;
                const rawRow = rawRows[rowIndex] || row;
                const rowBgColor = dtGetRowColor(rawRow, rowColorRules);
                const hasRowColor = rowBgColor !== 'transparent';

                return (
                  <TableRow
                    key={`row-${rowId}-${rowIndex}`}
                    onMouseEnter={(e) => {
                      if (hasRowColor) e.currentTarget.style.backgroundColor = dtDarkenColor(rowBgColor);
                    }}
                    onMouseLeave={(e) => {
                      if (hasRowColor) e.currentTarget.style.backgroundColor = rowBgColor;
                    }}
                    sx={{
                      backgroundColor: hasRowColor ? `${rowBgColor} !important` : 'transparent',
                      borderLeft: hasRowColor ? `6px solid ${rowBgColor}` : 'none',
                      '&:hover': hasRowColor ? { backgroundColor: 'inherit !important' } : {}
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        width: '60px',
                        minWidth: '60px',
                        maxWidth: '60px',
                        fontWeight: 'bold',
                        backgroundColor: hasRowColor ? 'inherit' : '#fafafa',
                        fontSize: '0.75rem',
                        padding: '8px 12px',
                        position: 'sticky',
                        left: 0,
                        zIndex: '5 !important',
                        borderRight: '2px solid #e0e0e0'
                      }}
                    >
                      {rowIndex + 1}
                    </TableCell>
                    {columns.map((column, colIndex) => {
                      let value;
                      if (column.dataPath) {
                        if (column.dataPath.includes('.')) {
                          const pathParts = column.dataPath.split('.');
                          value = row;
                          for (const part of pathParts) {
                            if (value && typeof value === 'object') {
                              value = value[part];
                            } else {
                              value = undefined;
                              break;
                            }
                          }
                        } else {
                          value = row[column.dataPath];
                        }
                      } else {
                        value = row[column.key];
                      }

                      const cellColors = dtGetCellColors(rawRow, column.key, cellColorRules);

                      return (
                        <TableCell
                          key={`cell-${rowId}-${column.key}-${column.id}-${rowIndex}-${colIndex}`}
                          align={column.alignment || 'left'}
                          sx={{
                            width: getColumnWidth(column.width),
                            fontSize: '0.75rem',
                            padding: '8px 12px',
                            color: cellColors.fontColor,
                            backgroundColor: cellColors.backgroundColor || undefined,
                            fontWeight: cellColors.fontColor !== 'inherit' ? 600 : 'normal'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'inherit', fontWeight: 'inherit' }}>
                            {formatCellValue(value, column)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Data Table Interface
  const renderDataTable = (output) => {
    console.log('🎨 renderDataTable called with:', output);

    if (output.error && !output.tabs) {
      return (
        <Alert severity="error">
          <Typography variant="subtitle2">Table Display Error</Typography>
          <Typography variant="body2">{output.error}</Typography>
        </Alert>
      );
    }

    const tabs = output.tabs;
    const isMultiTab = tabs && tabs.length > 1;

    if (isMultiTab) {
      const safeTabIndex = Math.min(dataTableTabIndex, tabs.length - 1);
      const activeTabData = tabs[safeTabIndex];

      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Data Table
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={safeTabIndex}
              onChange={(e, newVal) => setDataTableTabIndex(newVal)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, idx) => (
                <Tab
                  key={idx}
                  label={`${tab.label || `Sheet ${idx + 1}`} (${tab.totalRows || 0})`}
                />
              ))}
            </Tabs>
          </Box>
          {activeTabData && renderSingleDataTablePanel(activeTabData)}
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Data Table ({output.totalRows || (output.rows || []).length} items)
        </Typography>
        {renderSingleDataTablePanel(output)}
      </Box>
    );
  };

  // Detailed Report Interface
  const renderDetailedReport = (output) => {
    const sections = output.sections || [];
    
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Detailed Report
        </Typography>
        {sections.map((section, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {section.title}
            </Typography>
            <Typography variant="body1" paragraph>
              {section.content}
            </Typography>
            {section.data && (
              <Box sx={{ mt: 2 }}>
                <pre style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', overflow: 'auto' }}>
                  {JSON.stringify(section.data, null, 2)}
                </pre>
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    );
  };

  // Dashboard Interface
  const renderDashboard = (output) => {
    const widgets = output.widgets || [];
    
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Dashboard
        </Typography>
        <Grid container spacing={2}>
          {widgets.map((widget, index) => (
                         <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
               <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {widget.title}
                  </Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {widget.value}
                  </Typography>
                  {widget.description && (
                    <Typography variant="body2" color="textSecondary">
                      {widget.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Custom Template Interface
  const renderCustomTemplate = (output) => {
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Custom Template
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body1" component="div">
            {output.template || 'No template content available'}
          </Typography>
        </Paper>
      </Box>
    );
  };

  // Default Output Renderer
  const renderDefaultOutput = (result) => {
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Raw Output
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Paper>
      </Box>
    );
  };

  // Export Options Renderer
  const renderExportOptions = (exportConfig) => {
    if (!exportConfig || !exportConfig.enabled) return null;

    const formats = exportConfig.formats || ['json'];
    
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="h6" gutterBottom>
          Export Options
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {formats.map((format) => (
            <Button
              key={format}
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleExportData(format, exportConfig)}
            >
              Export as {format.toUpperCase()}
            </Button>
          ))}
        </Box>
      </Box>
    );
  };

  const handleExportData = async (format, exportConfig) => {
    try {
      console.log('Exporting data:', { format, exportConfig, execution });
      
      // Find the End node step result for export
      const endStepResult = stepResults.find(result => 
        result.stepType === 'end' && result.status === 'completed'
      );
      
      if (!endStepResult || !endStepResult.output) {
        alert('No data available to export');
        return;
      }
      
      const result = endStepResult.output;
      const formattedOutput = result.formattedOutput;
      
      // Prepare export data based on interface type
      let exportData = {};
      let fileName = `execution_${executionId.slice(-8)}_${new Date().toISOString().split('T')[0]}`;
      
      // Extract data based on interface type
      if (formattedOutput) {
        const interfaceType = result.interfaceType || 'summary_card';
        
        switch (interfaceType) {
          case 'data_table':
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              headerFields: formattedOutput.headerFields || [],
              includeHeaderFieldsInExport: formattedOutput.includeHeaderFieldsInExport || false,
              columns: formattedOutput.columns || [],
              rows: formattedOutput.rows || [],
              rawRows: formattedOutput.rawRows || formattedOutput.rows || [],
              rowColorRules: formattedOutput.rowColorRules || [],
              cellColorRules: formattedOutput.cellColorRules || [],
              totalRows: formattedOutput.totalRows || formattedOutput.rows?.length || 0,
              tabs: formattedOutput.tabs || null
            };
            break;
            
          case 'summary_card':
            // Export summary fields
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              fields: formattedOutput.fields || []
            };
            break;
            
          case 'detailed_report':
            // Export report sections
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              sections: formattedOutput.sections || []
            };
            break;
            
          case 'dashboard':
            // Export dashboard widgets
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              widgets: formattedOutput.widgets || []
            };
            break;
            
          default:
            // Export raw output
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              output: formattedOutput
            };
        }
      } else {
        // Fallback to raw result
        exportData = {
          title: 'Execution Results',
          executionId: executionId,
          timestamp: new Date().toISOString(),
          result: result
        };
      }
      
      // Export based on format
      switch (format.toLowerCase()) {
        case 'json':
          exportAsJSON(exportData, fileName);
          break;
          
        case 'csv':
          exportAsCSV(exportData, fileName);
          break;
          
        case 'excel':
        case 'xlsx':
          exportAsExcel(exportData, fileName);
          break;
          
        case 'pdf':
          alert('PDF export is not yet implemented. Please use JSON or CSV export.');
          break;
          
        default:
          exportAsJSON(exportData, fileName);
      }
      
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error.message}`);
    }
  };
  
  // Export as JSON
  const exportAsJSON = (data, fileName) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  
  // Export as CSV
  const exportAsCSV = (data, fileName) => {
    let csvContent = '';
    
    const writeCsvTable = (columns, rows, headerFields) => {
      let section = '';
      if (headerFields && headerFields.length > 0) {
        section += '"Summary Information"\n';
        headerFields.forEach(field => {
          const value = field.defaultValue ? resolveTemplateVariables(field.defaultValue) : (field.value || '-');
          section += `"${field.label || field.name}","${value}"\n`;
        });
        section += '\n';
      }
      section += columns.map(col => `"${col.label}"`).join(',') + '\n';
      rows.forEach(row => {
        const rowValues = columns.map(col => {
          let value;
          if (col.dataPath) {
            if (col.dataPath.includes('.')) {
              const pathParts = col.dataPath.split('.');
              value = row;
              for (const part of pathParts) {
                if (value && typeof value === 'object') {
                  value = value[part];
                } else {
                  value = undefined;
                  break;
                }
              }
            } else {
              value = row[col.dataPath];
            }
          } else {
            value = row[col.key];
          }
          const formattedValue = formatCellValue(value, col);
          return `"${String(formattedValue).replace(/"/g, '""')}"`;
        });
        section += rowValues.join(',') + '\n';
      });
      return section;
    };

    if (data.tabs && data.tabs.length > 1) {
      csvContent += `"${data.title}"\n`;
      csvContent += `"Execution ID: ${data.executionId}"\n`;
      csvContent += `"Export Date: ${new Date(data.timestamp).toLocaleString()}"\n\n`;
      data.tabs.forEach((tab, idx) => {
        csvContent += `"--- ${tab.label || `Sheet ${idx + 1}`} ---"\n`;
        csvContent += writeCsvTable(tab.columns || [], tab.rows || [], tab.headerFields);
        csvContent += '\n';
      });
    } else if (data.rows && data.columns) {
      csvContent += `"${data.title}"\n`;
      csvContent += `"Execution ID: ${data.executionId}"\n`;
      csvContent += `"Export Date: ${new Date(data.timestamp).toLocaleString()}"\n\n`;
      csvContent += writeCsvTable(data.columns, data.rows, data.headerFields);
    }
    else if (data.fields) {
      csvContent += `"${data.title}"\n`;
      csvContent += `"Execution ID: ${data.executionId}"\n`;
      csvContent += `"Export Date: ${new Date(data.timestamp).toLocaleString()}"\n\n`;
      csvContent += '"Field","Value"\n';
      
      data.fields.forEach(field => {
        csvContent += `"${field.label}","${field.formattedValue || field.value || '-'}"\n`;
      });
    }
    // Handle other interface types
    else {
      csvContent += `"${data.title}"\n`;
      csvContent += `"Execution ID: ${data.executionId}"\n`;
      csvContent += `"Export Date: ${new Date(data.timestamp).toLocaleString()}"\n\n`;
      csvContent += '"Data"\n';
      csvContent += `"${JSON.stringify(data).replace(/"/g, '""')}"\n`;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  
  const exportAsExcel = async (data, fileName) => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();

      const evalSingle = (val, op, expected) => {
        switch (op) {
          case 'equals': return val == expected; // eslint-disable-line eqeqeq
          case 'not_equals': return val != expected; // eslint-disable-line eqeqeq
          case 'contains': return String(val).includes(String(expected));
          case 'not_contains': return !String(val).includes(String(expected));
          case 'exists': return val !== undefined && val !== null && val !== '';
          case 'not_exists': return val === undefined || val === null || val === '';
          case 'greater_than': return parseFloat(val) > parseFloat(expected);
          case 'less_than': return parseFloat(val) < parseFloat(expected);
          case 'is_true': return val === true || val === 'true' || val === 1;
          case 'is_false': return val === false || val === 'false' || val === 0;
          default: return false;
        }
      };
      const resolveRV = (text, rawRow) => {
        if (typeof text !== 'string') return text;
        return text.replace(/\{\{([\w.]+)\}\}/g, (m, v) => rawRow && rawRow[v] !== undefined ? String(rawRow[v]) : m);
      };
      const evalCond = (rawRow, cond) => {
        if (!cond || !cond.variable) return false;
        let rv = cond.value;
        if (typeof rv === 'string' && rv.includes('{{')) rv = resolveRV(rv, rawRow);
        const first = evalSingle(rawRow[cond.variable], cond.operator, rv);
        if (!cond.secondVariable || !cond.secondOperator) return first;
        let rv2 = cond.secondValue;
        if (typeof rv2 === 'string' && rv2.includes('{{')) rv2 = resolveRV(rv2, rawRow);
        const second = evalSingle(rawRow[cond.secondVariable], cond.secondOperator, rv2);
        return cond.logicalOperator === 'OR' ? (first || second) : (first && second);
      };
      const hexArgb = (hex) => {
        if (!hex) return undefined;
        hex = hex.replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        return 'FF' + hex.toUpperCase();
      };

      const writeDataTableSheet = (ws, sheetColumns, sheetRows, sheetRawRows, sheetRowColorRules, sheetCellColorRules, sheetHdrFields, sheetIncludeHdr) => {
        let tableStartRow = 1;

        if (sheetIncludeHdr && sheetHdrFields.length > 0) {
          sheetHdrFields.forEach((field) => {
            const label = field.label || field.name || '';
            const value = field.value !== undefined && field.value !== null ? String(field.value) : '';
            const fieldRow = ws.addRow([label, value]);
            fieldRow.getCell(1).font = { bold: true, color: { argb: 'FF555555' } };
            if (field.fontColor) {
              fieldRow.getCell(2).font = { color: { argb: hexArgb(field.fontColor) } };
            }
          });
          ws.addRow([]);
          tableStartRow = sheetHdrFields.length + 2;
          ws.getColumn(1).width = Math.max(ws.getColumn(1).width || 0, 20);
          ws.getColumn(2).width = Math.max(ws.getColumn(2).width || 0, 30);
        }

        const headerRow = ws.addRow(sheetColumns.map(c => c.label || c.key));
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: 'FF333333' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
          cell.border = { bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } } };
        });

        sheetColumns.forEach((col, i) => {
          ws.getColumn(i + 1).width = Math.max(
            ws.getColumn(i + 1).width || 0,
            col.width === 'wide' ? 25 : col.width === 'narrow' ? 10 : 18
          );
        });

        sheetRows.forEach((row, rowIndex) => {
          const rawRow = sheetRawRows[rowIndex] || row;
          const cellValues = sheetColumns.map(col => {
            const value = row[col.key];
            if ((col.type === 'number' || col.type === 'currency') && !isNaN(value)) return Number(value);
            return value;
          });

          const dataRow = ws.addRow(cellValues);

          let rowColor = null;
          for (const rule of sheetRowColorRules) {
            if (evalCond(rawRow, rule.condition)) { rowColor = hexArgb(rule.color); break; }
          }
          if (rowColor) {
            dataRow.eachCell({ includeEmpty: true }, (cell) => {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowColor } };
            });
          }

          sheetColumns.forEach((col, colIdx) => {
            const applicable = sheetCellColorRules.filter(r => r.fieldKey === col.key);
            for (const rule of applicable) {
              if (evalCond(rawRow, rule.condition)) {
                const cell = dataRow.getCell(colIdx + 1);
                if (rule.color) cell.font = { ...(cell.font || {}), bold: true, color: { argb: hexArgb(rule.color) } };
                if (rule.backgroundColor) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hexArgb(rule.backgroundColor) } };
                break;
              }
            }
          });
        });

        if (sheetColumns.length > 0) {
          ws.autoFilter = { from: { row: tableStartRow, column: 1 }, to: { row: tableStartRow, column: sheetColumns.length } };
        }
      };

      if (data.tabs && data.tabs.length > 1) {
        data.tabs.forEach((tab, idx) => {
          const sheetName = (tab.label || `Sheet ${idx + 1}`).substring(0, 31);
          const ws = workbook.addWorksheet(sheetName);
          writeDataTableSheet(
            ws,
            tab.columns || [],
            tab.rows || [],
            tab.rawRows || tab.rows || [],
            tab.rowColorRules || [],
            tab.cellColorRules || [],
            tab.headerFields || [],
            tab.includeHeaderFieldsInExport
          );
        });
      } else if (data.columns && data.rows) {
        const ws = workbook.addWorksheet('Data Table');
        writeDataTableSheet(
          ws,
          data.columns,
          data.rows,
          data.rawRows || data.rows,
          data.rowColorRules || [],
          data.cellColorRules || [],
          data.headerFields || [],
          data.includeHeaderFieldsInExport && (data.headerFields || []).length > 0
        );
      } else if (data.fields) {
        const ws = workbook.addWorksheet('Summary Data');
        ws.addRow(['Field', 'Value']);
        data.fields.forEach(f => ws.addRow([f.label, f.formattedValue || f.value || '-']));
        ws.getRow(1).font = { bold: true };
      } else {
        const ws = workbook.addWorksheet('Data');
        ws.addRow(['Property', 'Value']);
        Object.entries(data).forEach(([k, v]) => {
          if (!['title', 'executionId', 'timestamp'].includes(k)) {
            ws.addRow([k, typeof v === 'object' ? JSON.stringify(v) : v]);
          }
        });
        ws.getRow(1).font = { bold: true };
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Excel export failed. Exporting as CSV instead.');
      exportAsCSV(data, fileName);
    }
  };

  // Render steps execution details
  const renderStepsDetails = () => {
    if (!stepResults || stepResults.length === 0) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Step Details Available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Step execution details were not found for this execution.
          </Typography>
        </Paper>
      );
    }

    return stepResults.map((result, index) => {
      const hasPerformanceWarnings = result.performanceMetrics?.warnings && result.performanceMetrics.warnings.length > 0;
      const isSlowStep = result.duration && result.duration > 5000; // Steps taking more than 5 seconds
      
      return (
        <Accordion key={index} defaultExpanded={index === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              {getStepIcon(result.stepType, result.status)}
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {result.stepName || `Step ${index + 1}`}
                  </Typography>
                  {isSlowStep && result.status === 'completed' && (
                    <Chip 
                      label="Slow" 
                      size="small" 
                      color="warning" 
                      icon={<SpeedIcon />}
                      sx={{ height: 20 }}
                    />
                  )}
                  {hasPerformanceWarnings && (
                    <Chip 
                      label={`${result.performanceMetrics.warnings.length} Warning${result.performanceMetrics.warnings.length > 1 ? 's' : ''}`}
                      size="small" 
                      color="error" 
                      icon={<WarningIcon />}
                      sx={{ height: 20 }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {result.stepType} • {formatDuration(result.duration)}
                </Typography>
              </Box>
              <Chip
                label={result.status}
                color={getStatusColor(result.status)}
                size="small"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Performance Warnings - Show at top if present */}
              {hasPerformanceWarnings && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Performance Issues Detected:
                    </Typography>
                    {result.performanceMetrics.warnings.map((warning, idx) => (
                      <Typography key={idx} variant="body2" display="block">
                        • {warning.message}
                        {warning.threshold && warning.actual && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (Threshold: {warning.threshold}ms, Actual: {warning.actual}ms)
                          </Typography>
                        )}
                      </Typography>
                    ))}
                  </Alert>
                </Grid>
              )}
              
              {/* Step Details */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Step Details
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">Status:</Typography>
                        <Chip label={result.status} color={getStatusColor(result.status)} size="small" />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">Duration:</Typography>
                        <Typography variant="body2">{formatDuration(result.duration)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">Type:</Typography>
                        <Typography variant="body2">{result.stepType}</Typography>
                      </Box>
                      {result.startedAt && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="textSecondary">Started:</Typography>
                          <Typography variant="body2">
                            {new Date(result.startedAt).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Step Output */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2">
                        Output
                      </Typography>
                      {result.output && (
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewStepResult(result)}
                        >
                          View Details
                        </Button>
                      )}
                    </Box>
                    
                    {/* S3 Storage Indicator for Step */}
                    {result.output && countS3References(result.output) > 0 && (
                      <Alert 
                        severity="info" 
                        icon={<CloudIcon />}
                        sx={{ mb: 2, py: 0.5 }}
                      >
                        <Typography variant="body2">
                          This step output contains <strong>{countS3References(result.output)} variable(s)</strong> stored in S3 cloud storage.
                        </Typography>
                      </Alert>
                    )}
                    
                    {result.error ? (
                      <Box>
                        <Alert severity="error">
                          <Typography variant="subtitle2">Error:</Typography>
                          <Typography variant="body2">{result.error}</Typography>
                        </Alert>
                        
                        {/* Show diagnostics for file processing errors */}
                        {result.output?.results?.some(r => r.diagnostics) && (
                          <Paper variant="outlined" sx={{ p: 2, mt: 2, backgroundColor: '#fff3e0', borderColor: '#ff9800' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#e65100', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WarningIcon fontSize="small" />
                              Diagnostic Information
                            </Typography>
                            {result.output.results.filter(r => r.diagnostics).map((fileResult, idx) => (
                              <Box key={idx} sx={{ mb: idx < result.output.results.filter(r => r.diagnostics).length - 1 ? 2 : 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  File: {fileResult.fileName}
                                </Typography>
                                {fileResult.diagnostics && (
                                  <Box sx={{ pl: 2 }}>
                                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', backgroundColor: '#fff', p: 1, borderRadius: 1, mb: 1 }}>
                                      <strong>Attempted Path:</strong> {fileResult.diagnostics.attemptedPath}
                                    </Typography>
                                    <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                                      <strong>Timestamp:</strong> {new Date(fileResult.diagnostics.timestamp).toLocaleString()}
                                    </Typography>
                                    
                                    {fileResult.diagnostics.fileInfo && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                                          File Info:
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                          <Typography variant="caption" component="div">
                                            • Uploaded to S3: {fileResult.diagnostics.fileInfo.uploadedToS3 ? 'Yes' : 'No'}
                                          </Typography>
                                          {fileResult.diagnostics.fileInfo.s3Key && (
                                            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                                              • S3 Key: {fileResult.diagnostics.fileInfo.s3Key}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.fileInfo.fileSize && (
                                            <Typography variant="caption" component="div">
                                              • Size: {formatFileSize(fileResult.diagnostics.fileInfo.fileSize)}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.fileInfo.uploadedAt && (
                                            <Typography variant="caption" component="div">
                                              • Uploaded: {new Date(fileResult.diagnostics.fileInfo.uploadedAt).toLocaleString()}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                    
                                    {fileResult.diagnostics.uploadDirExists !== undefined && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                                          Upload Directory Status:
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                          <Typography variant="caption" component="div">
                                            • Exists: {fileResult.diagnostics.uploadDirExists ? 'Yes' : 'No'}
                                          </Typography>
                                          {fileResult.diagnostics.uploadDirExists && (
                                            <Typography variant="caption" component="div">
                                              • Files in directory: {fileResult.diagnostics.filesInUploadDir}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.matchingFiles && fileResult.diagnostics.matchingFiles.length > 0 && (
                                            <Typography variant="caption" component="div">
                                              • Matching files found: {fileResult.diagnostics.matchingFiles.join(', ')}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.uploadDirError && (
                                            <Typography variant="caption" component="div" color="error">
                                              • Error: {fileResult.diagnostics.uploadDirError}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                    
                                    {fileResult.diagnostics.parentDirExists !== undefined && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                                          Parent Directory:
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                          <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                            {fileResult.diagnostics.parentDirPath}
                                          </Typography>
                                          <Typography variant="caption" component="div">
                                            • Exists: {fileResult.diagnostics.parentDirExists ? 'Yes' : 'No'}
                                          </Typography>
                                          {fileResult.diagnostics.parentDirExists && (
                                            <Typography variant="caption" component="div">
                                              • Files in directory: {fileResult.diagnostics.filesInParentDir}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.parentDirError && (
                                            <Typography variant="caption" component="div" color="error">
                                              • Error: {fileResult.diagnostics.parentDirError}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                    
                                    <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                                      <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        💡 Possible Causes:
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        • File was uploaded but deleted before processing (check timing)
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        • File upload may have failed silently
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        • Server restart or process manager may have cleared temp files
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        • Consider enabling S3 upload in collect_input step configuration
                                      </Typography>
                                    </Alert>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                    ) : result.output ? (
                      <Box>
                        {/* Show S3 references summary if any */}
                        {Object.entries(result.output).some(([, v]) => isS3Reference(v)) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CloudIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                              S3-Stored Variables in Output:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {Object.entries(result.output)
                                .filter(([, v]) => isS3Reference(v))
                                .map(([key, v]) => (
                                  <Chip
                                    key={key}
                                    size="small"
                                    icon={<CloudIcon />}
                                    label={`${key} (${((v.originalSize || 0) / (1024 * 1024)).toFixed(2)} MB)`}
                                    sx={{ 
                                      backgroundColor: '#e3f2fd', 
                                      color: '#1565c0',
                                      '& .MuiChip-icon': { color: '#1565c0' }
                                    }}
                                  />
                                ))}
                            </Box>
                          </Box>
                        )}
                        <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                            {JSON.stringify(result.output, null, 2)}
                          </pre>
                        </Paper>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                        No output data available
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Performance Breakdown - Show if available */}
              {result.performanceMetrics && (
                <Grid size={{ xs: 12 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                        <SpeedIcon color="primary" /> Performance Breakdown
                      </Typography>
                      
                      {/* Time Breakdown */}
                      {result.performanceMetrics.breakdown && result.performanceMetrics.breakdown.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Time Breakdown:
                          </Typography>
                          <List dense>
                            {result.performanceMetrics.breakdown.map((item, idx) => (
                              <ListItem key={idx} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography variant="body2">{item.operation}</Typography>
                                      <Box display="flex" alignItems="center" gap={2}>
                                        <Chip 
                                          label={`${item.percentage}%`} 
                                          size="small" 
                                          color={item.percentage > 50 ? 'error' : item.percentage > 25 ? 'warning' : 'default'} 
                                          variant="outlined"
                                        />
                                        <Typography variant="body2" fontWeight="bold">
                                          {formatDuration(item.duration)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  }
                                  secondary={item.details}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      
                      {/* Detailed Metrics */}
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Detailed Metrics:
                      </Typography>
                      <Grid container spacing={2}>
                        {/* Pre-execution Operations */}
                        {(result.performanceMetrics.cacheCleanupTime > 0 || 
                          result.performanceMetrics.authenticationCheckTime > 0 || 
                          result.performanceMetrics.validationTime > 0) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="Pre-execution Operations" size="small" color="info" />
                            </Grid>
                            {result.performanceMetrics.cacheCleanupTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Cache Cleanup</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.cacheCleanupTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.authenticationCheckTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Authentication Check</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.authenticationCheckTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.validationTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Validation Time</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.validationTime)}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* File Operations */}
                        {(result.performanceMetrics.fileUploadTime > 0 || 
                          result.performanceMetrics.fileProcessingTime > 0) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="File Operations" size="small" color="secondary" />
                            </Grid>
                            {result.performanceMetrics.fileUploadTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">File Upload</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.fileUploadTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.fileProcessingTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">File Processing</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.fileProcessingTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.fileCount > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Files Processed</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.fileCount} files
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.totalFileSize > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Total Size</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatFileSize(result.performanceMetrics.totalFileSize)}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* AI Processing */}
                        {result.performanceMetrics.aiProcessingTime > 0 && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="AI Processing" size="small" color="success" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Typography variant="caption" color="text.secondary">AI Processing Time</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(result.performanceMetrics.aiProcessingTime)}
                              </Typography>
                            </Grid>
                            {result.performanceMetrics.aiModelUsed && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Model</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.aiModelUsed}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.aiTokens > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Tokens Used</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.aiTokens.toLocaleString()}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* Network & API */}
                        {(result.performanceMetrics.networkLatency > 0 || 
                          result.performanceMetrics.apiCallTime > 0) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="Network & API" size="small" color="warning" />
                            </Grid>
                            {result.performanceMetrics.networkLatency > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Network Latency</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.networkLatency)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.apiCallTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">API Call Time</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.apiCallTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.apiCallCount > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">API Calls</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.apiCallCount}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* Learning Corrections */}
                        {result.performanceMetrics.learningCorrectionsTime > 0 && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="Learning Corrections" size="small" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Typography variant="caption" color="text.secondary">Learning Time</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(result.performanceMetrics.learningCorrectionsTime)}
                              </Typography>
                            </Grid>
                            {result.performanceMetrics.learningCorrectionsCount > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Corrections Captured</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.learningCorrectionsCount}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* OCR & Vision Processing (Document OCR & Vision steps) */}
                        {(result.performanceMetrics.ocrEngine || result.performanceMetrics.visionModel) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="OCR & Vision Processing" size="small" color="info" />
                            </Grid>
                            {result.performanceMetrics.ocrEngine && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">OCR Engine</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.ocrEngine}
                                  {result.performanceMetrics.awsTextract && ' (AWS Textract)'}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.visionModel && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Vision Model</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.visionModel}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.visionMethod && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Vision Method</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.visionMethod}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.processingMode && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Processing Mode</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.processingMode}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* LLM Processing (AI Field Mapping steps) */}
                        {result.performanceMetrics.llmModel && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="LLM Processing" size="small" color="secondary" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Typography variant="caption" color="text.secondary">LLM Model</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {result.performanceMetrics.llmModel}
                              </Typography>
                            </Grid>
                            {result.performanceMetrics.maxTokens && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Max Tokens</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.maxTokens.toLocaleString()}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.includeConfidenceScores !== undefined && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Confidence Scores</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.includeConfidenceScores ? 'Enabled' : 'Disabled'}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  // Render files tab
  const renderFilesTab = () => {
    return (
      <Box>
        
        {/* Input Files Section */}
        {execution?.inputData?.files && execution.inputData.files.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Input Files
            </Typography>
            <List sx={{ p: 0 }}>
              {execution.inputData.files.map((file, index) => (
                <Card key={index} sx={{ mb: 1 }}>
                  <ListItem
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                    onClick={() => handleFileClick(file)}
                  >
                    <ListItemIcon>
                      {isPreviewableFile(file.originalName) ? (
                        <Box sx={{ position: 'relative' }}>
                          <AssignmentIcon color="primary" />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -2,
                              right: -2,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: 'success.main',
                              border: '1px solid white'
                            }}
                          />
                        </Box>
                      ) : (
                        <AssignmentIcon color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="medium">
                          {file.originalName}
                        </Typography>
                      }
                    />
                    <Box sx={{ ml: 2, flex: 1 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        {file.fileType} • {formatFileSize(file.fileSize || 0)}
                        {isPreviewableFile(file.originalName) ? ' • Click to preview' : ''}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        Uploaded: {new Date(file.uploadedAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {file.filename || file.fileId || 'No ID'} | Type: {file.fileType || 'Unknown'}
                      </Typography>
                    </Box>
                  </ListItem>
                </Card>
              ))}
            </List>
          </Box>
        )}

        {/* Output Files Section */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Output Files
        </Typography>
        {outputFiles.length > 0 ? (
          <List sx={{ p: 0 }}>
            {outputFiles.map((file, index) => (
              <Card key={index} sx={{ mb: 1 }}>
                <ListItem
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                  onClick={() => handleFileClick(file)}
                >
                  <ListItemIcon>
                    {isPreviewableFile(file.fileName) ? (
                      <Box sx={{ position: 'relative' }}>
                        <AssignmentIcon color="primary" />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -2,
                            right: -2,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            border: '1px solid white'
                          }}
                        />
                      </Box>
                    ) : (
                      <AssignmentIcon color="primary" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" fontWeight="medium">
                        {file.fileName}
                      </Typography>
                    }
                  />
                  <Box sx={{ ml: 2, flex: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                      {file.fileType} • {formatFileSize(file.fileSize || 0)}
                      {isPreviewableFile(file.fileName) ? ' • Click to preview' : ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                      {new Date(file.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      ID: {file.fileId || 'No ID'} | Type: {file.fileType || 'Unknown'}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadFile(file.fileId, file.fileName);
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </ListItem>
              </Card>
            ))}
          </List>
        ) : (
          <Alert severity="info">
            No output files were generated during this execution.
          </Alert>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box 
        sx={{
          width: '100%',
          overflowX: 'hidden'
        }}
      >
        <Box 
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            mt: isMobile ? 2 : 4,
            px: isMobile ? 1 : 2
          }}
        >
          <BreadcrumbsNav />
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={60} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error || !execution) {
    return (
      <Box 
        sx={{
          width: '100%',
          overflowX: 'hidden'
        }}
      >
        <Box 
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            mt: isMobile ? 2 : 4,
            px: isMobile ? 1 : 2
          }}
        >
          <BreadcrumbsNav />
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Execution not found'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              // Navigate to project page with Executions tab active (tab index 1)
              navigate(`/company/${companyId}/project/${projectId}?tab=1`);
            }}
            fullWidth={isMobile}
          >
            Back to Project
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        width: '100%',
        overflowX: 'hidden'
      }}
    >
      <Box 
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          mt: isMobile ? 2 : 4,
          px: isMobile ? 1 : 2
        }}
      >
        <BreadcrumbsNav />
      
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between" 
        alignItems={isMobile ? "stretch" : "center"}
        gap={isMobile ? 2 : 0}
        mb={isMobile ? 2 : 4}
        sx={{ minWidth: 0 }} // Prevent overflow
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
            {execution.status === 'completed' ? 'Execution Results' : 'Execution Details'}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant={isMobile ? "caption" : "body1"} color="textSecondary">
              Execution #{executionId.slice(-8)} • {execution.projectName || 'Project'}
            </Typography>
            {autoRefresh && (
              <Chip 
                label="Auto-refreshing" 
                color="info" 
                size="small" 
                icon={<RefreshIcon />}
              />
            )}
          </Box>
          {!isMobile && (
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', mt: 0.5 }}>
              ID: {executionId}
            </Typography>
          )}
        </Box>
        <Box 
          display="flex" 
          flexDirection={isMobile ? "column" : "row"} 
          gap={isMobile ? 1 : 2}
          sx={{ 
            width: isMobile ? "100%" : "auto",
            minWidth: 0, // Prevent overflow
            flexShrink: 0
          }}
        >
          <Button
            variant="outlined"
            startIcon={isMobile ? null : <ArrowBackIcon />}
            onClick={() => {
              // Navigate to project page with Executions tab active (tab index 1)
              navigate(`/company/${companyId}/project/${projectId}?tab=1`);
            }}
            fullWidth={isMobile}
            size="small"
            sx={{
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              py: isMobile ? 0.5 : 0.75
            }}
          >
            {isMobile ? <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} /> : null}
            Back to Project
          </Button>
          <Button
            variant="outlined"
            startIcon={isMobile ? null : <RefreshIcon />}
            onClick={fetchExecutionDetails}
            fullWidth={isMobile}
            size="small"
            sx={{
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              py: isMobile ? 0.5 : 0.75
            }}
          >
            {isMobile ? <RefreshIcon fontSize="small" sx={{ mr: 0.5 }} /> : null}
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Execution Summary */}
      <Paper elevation={1} sx={{ p: isMobile ? 2 : 3, mb: isMobile ? 2 : 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box textAlign="center">
              <Avatar sx={{ bgcolor: getStatusColor(execution.status) + '.main', mx: 'auto', mb: 1 }}>
                {execution.status === 'completed' ? <CheckCircleIcon /> : <ErrorIcon />}
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {execution.status === 'completed' ? 'Completed' : execution.status}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Status
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="primary">
                {stepResults?.length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Processing Steps
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {formatDuration(
                  // For in-progress executions, prefer actualProcessingTime (completed steps only)
                  // For completed/failed executions, use actualProcessingTime if available, otherwise fall back to duration
                  (execution.status === 'in_progress' || execution.status === 'waiting_input') 
                    ? (execution.actualProcessingTime || 0)
                    : (execution.actualProcessingTime || execution.duration)
                )}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Duration
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {execution.filesCount || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Files Processed
              </Typography>
            </Box>
          </Grid>
          {/* Token Usage Display */}
          {execution?.metrics?.billableObjectsCumulative && (
            <Grid size={{ xs: 12, md: 2 }}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  🪙 {execution.metrics.billableObjectsCumulative}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Tokens Used
                </Typography>
              </Box>
            </Grid>
          )}
          {/* Billable Events Display */}
          {execution?.metrics?.billableEvents && (
            <Grid size={{ xs: 12, md: 2 }}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight="bold" color="secondary.main">
                  📊 {execution.metrics.billableEvents}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Billable Events
                </Typography>
              </Box>
            </Grid>
          )}
          {/* S3 Cloud Storage Display */}
          {(execution?.metrics?.s3VariablesCount > 0 || 
            Object.values(execution?.variables || {}).some(v => v && typeof v === 'object' && v._s3Ref === true)) && (
            <Grid size={{ xs: 12, md: 2 }}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#1565c0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <CloudIcon sx={{ fontSize: 32 }} />
                  {execution?.metrics?.s3VariablesCount || 
                    Object.values(execution?.variables || {}).filter(v => v && typeof v === 'object' && v._s3Ref === true).length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  S3 Variables
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Execution ID Section */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
            🔍 Execution Information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="textSecondary">Execution ID:</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500 }}>
                {executionId}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="textSecondary">Project:</Typography>
              <Typography variant="body1">
                {execution.projectName || 'Unknown Project'}
              </Typography>
            </Grid>
            {execution?.pwaVersion && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">PWA Version:</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500 }}>
                  v{execution.pwaVersion}
                  {execution.pwaBuildDate && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (Build: {execution.pwaBuildDate})
                    </Typography>
                  )}
                </Typography>
              </Grid>
            )}
            {execution?.variables?.userFullName && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">User:</Typography>
                <Typography variant="body1">
                  {execution.variables.userFullName}
                </Typography>
              </Grid>
            )}
            {execution?.variables?.userAdditionalInfo && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">Additional Info:</Typography>
                <Typography variant="body1">
                  {execution.variables.userAdditionalInfo}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="textSecondary">Started At:</Typography>
            <Typography variant="body1">
              {new Date(execution.startedAt).toLocaleString()}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="textSecondary">Completed At:</Typography>
            <Typography variant="body1">
              {execution.completedAt ? new Date(execution.completedAt).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for Results, Steps, and Files */}
      <Paper elevation={1}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab 
            icon={execution?.status === 'completed' ? <CheckCircleIcon /> : <InfoIcon />} 
            label={execution?.status === 'completed' ? 'Results' : 'Status'} 
          />
          <Tab icon={<TimelineIcon />} label="Execution Steps" />
          <Tab icon={<AssignmentIcon />} label="Files" />
          <Tab 
            icon={<DataObjectIcon />} 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Variables
                {execution?.metrics?.s3VariablesCount > 0 && (
                  <Chip 
                    size="small" 
                    icon={<CloudIcon sx={{ fontSize: '14px !important' }} />}
                    label={execution.metrics.s3VariablesCount}
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      backgroundColor: '#e3f2fd',
                      color: '#1565c0',
                      '& .MuiChip-icon': { color: '#1565c0' }
                    }} 
                  />
                )}
              </Box>
            } 
          />
          <Tab icon={<AttachMoneyIcon />} label="Cost Estimate" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Results Tab */}
          {activeTab === 0 && (
            <Box>
              {renderExecutionResults()}
            </Box>
          )}

          {/* Steps Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={3}>
                Step-by-Step Execution Details
              </Typography>
              {renderStepsDetails()}
            </Box>
          )}

          {/* Files Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={3}>
                Files
              </Typography>
              {renderFilesTab()}
            </Box>
          )}

          {/* Variables Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={2}>
                Execution Variables
              </Typography>
              
              {/* S3 Storage Summary */}
              {execution?.metrics?.s3VariablesCount > 0 && (
                <Alert 
                  severity="info" 
                  icon={<CloudDoneIcon />}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body2">
                    <strong>{execution.metrics.s3VariablesCount} variable(s)</strong> are stored in S3 cloud storage to optimize performance.
                    Large data is automatically offloaded to reduce document size.
                  </Typography>
                </Alert>
              )}

              {execution?.variables && Object.keys(execution.variables).length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Variable Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Storage</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Value / Preview</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(execution.variables).map(([varName, value]) => {
                        const isS3Ref = value && typeof value === 'object' && value._s3Ref === true;
                        const valueType = isS3Ref ? 'S3 Reference' : 
                                         Array.isArray(value) ? `Array[${value.length}]` :
                                         value === null ? 'null' :
                                         typeof value === 'object' ? 'Object' :
                                         typeof value;
                        
                        const getValuePreview = () => {
                          if (isS3Ref) {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  size="small"
                                  icon={<CloudIcon />}
                                  label={`${((value.originalSize || 0) / (1024 * 1024)).toFixed(2)} MB`}
                                  sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  Uploaded: {value.uploadedAt ? new Date(value.uploadedAt).toLocaleString() : 'N/A'}
                                </Typography>
                              </Box>
                            );
                          }
                          if (Array.isArray(value)) {
                            if (value.length === 0) return '[]';
                            const preview = JSON.stringify(value[0]).substring(0, 100);
                            return `[${preview}${preview.length >= 100 ? '...' : ''}, ...]`;
                          }
                          if (typeof value === 'object' && value !== null) {
                            const preview = JSON.stringify(value).substring(0, 150);
                            return preview.length >= 150 ? preview + '...' : preview;
                          }
                          return String(value).substring(0, 200);
                        };
                        
                        return (
                          <TableRow key={varName} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                {varName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                size="small" 
                                label={valueType}
                                sx={{ 
                                  fontSize: '0.7rem',
                                  backgroundColor: isS3Ref ? '#fff3e0' : '#f5f5f5'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {isS3Ref ? (
                                <Chip 
                                  size="small"
                                  icon={<CloudIcon sx={{ fontSize: '14px !important' }} />}
                                  label="S3 Cloud"
                                  sx={{ 
                                    backgroundColor: '#e3f2fd',
                                    color: '#1565c0',
                                    '& .MuiChip-icon': { color: '#1565c0' }
                                  }}
                                />
                              ) : (
                                <Chip 
                                  size="small"
                                  icon={<StorageIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Database"
                                  sx={{ 
                                    backgroundColor: '#e8f5e9',
                                    color: '#2e7d32',
                                    '& .MuiChip-icon': { color: '#2e7d32' }
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: isS3Ref ? 'inherit' : 'monospace',
                                  fontSize: '0.8rem',
                                  maxWidth: '400px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {getValuePreview()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No variables stored for this execution.
                </Alert>
              )}

              {/* S3 Variables Detail Section */}
              {execution?.metrics?.s3Variables && execution.metrics.s3Variables.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudIcon color="primary" />
                    S3 Cloud Storage Details
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      {execution.metrics.s3Variables.map((s3Var, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Card variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', color: '#1565c0' }}>
                                {s3Var.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Size: {((s3Var.originalSize || 0) / (1024 * 1024)).toFixed(2)} MB
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Key: {s3Var.s3Key?.split('/').pop() || 'N/A'}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                      💡 S3-stored variables are automatically fetched when needed for exports or further processing.
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {/* Cost Estimate Tab */}
          {activeTab === 4 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={1}>
                Estimated Execution Cost
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Rough estimate based on LLM token usage, AWS EC2 t3.medium (ap-southeast-1), and MongoDB Atlas on AWS Singapore — 
                infrastructure costs are pro-rated to this execution's duration and shared across concurrent executions.
              </Typography>

              {/* Configuration Controls */}
              <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Infrastructure Settings
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>MongoDB Atlas Tier</InputLabel>
                      <Select
                        value={mongoTier}
                        label="MongoDB Atlas Tier"
                        onChange={(e) => setMongoTier(e.target.value)}
                      >
                        {Object.entries(MONGODB_ATLAS_TIERS).map(([tier, info]) => (
                          <MenuItem key={tier} value={tier}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{tier}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {info.ram} · {info.vcpu} · ${info.hourlyRate}/hr
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      AWS ap-southeast-1 (Singapore) — approx. on-demand pricing
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Concurrent Executions"
                      type="number"
                      inputProps={{ min: 1, max: 1000, step: 1 }}
                      value={concurrentExecutions}
                      onChange={(e) => setConcurrentExecutions(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Infrastructure costs are divided across this many simultaneous executions
                    </Typography>
                  </Grid>
                  {concurrentExecutions > 1 && (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Alert severity="info" sx={{ py: 0.5 }} icon={false}>
                        <Typography variant="caption">
                          With <strong>{concurrentExecutions}</strong> concurrent executions, each execution pays{' '}
                          <strong>1/{concurrentExecutions}</strong> of the EC2 + MongoDB costs, since the cluster is shared.
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {(() => {
                const estimate = calculateCostEstimate(mongoTier, concurrentExecutions);
                if (!estimate) {
                  return (
                    <Alert severity="info">
                      Cost estimation is not available — no execution data found.
                    </Alert>
                  );
                }

                const { totalCost, durationMs, llm, ec2, mongo, textract, sqs, s3, bedrock } = estimate;
                const hasLlmUsage = llm.breakdown.length > 0;
                const safeConurrent = Math.max(1, concurrentExecutions || 1);

                const allCostItems = [
                  { label: 'LLM / AI', value: llm.total, color: '#7c4dff' },
                  ...(textract.used ? [{ label: 'AWS Textract (OCR)', value: textract.total, color: '#d32f2f' }] : []),
                  { label: 'AWS S3', value: s3.total, color: '#0288d1' },
                  ...(bedrock.used ? [{ label: 'Bedrock Overhead', value: bedrock.total, color: '#e91e63' }] : []),
                  ...(sqs.used ? [{ label: 'AWS SQS', value: sqs.total, color: '#f57c00' }] : []),
                  { label: 'EC2 t3.medium (your share)', value: ec2.total, color: '#00bfa5' },
                  { label: `MongoDB Atlas ${mongoTier} (your share)`, value: mongo.total, color: '#ff6d00' },
                  { label: 'MongoDB Data Transfer', value: mongo.transfer.total, color: '#ff8f00' },
                ];
                const costItems = allCostItems.filter(i => i.value > 0);

                const fmtCost = (v) => v < 0.01 ? v.toFixed(6) : v.toFixed(4);

                return (
                  <>
                    {/* Total Cost Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        mb: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 2,
                        color: '#fff',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2 }}>
                        Total Estimated Cost
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">
                        ${fmtCost(totalCost)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                        USD · {formatDuration(durationMs)} execution
                        {safeConurrent > 1 ? ` · shared across ${safeConurrent} concurrent executions` : ''}
                      </Typography>
                    </Paper>

                    {/* Cost Breakdown Bars */}
                    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Cost Breakdown
                      </Typography>
                      {costItems.map((item) => {
                        const pct = totalCost > 0 ? (item.value / totalCost) * 100 : 0;
                        return (
                          <Box key={item.label} sx={{ mb: 2.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                              <Typography variant="body2" fontWeight="bold">
                                {item.label}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                ${fmtCost(item.value)}{' '}
                                <Typography component="span" variant="caption" color="text.secondary">
                                  ({pct.toFixed(1)}%)
                                </Typography>
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(pct, 100)}
                              sx={{
                                height: 10,
                                borderRadius: 5,
                                backgroundColor: '#f0f0f0',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 5,
                                  backgroundColor: item.color,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Paper>

                    <Grid container spacing={3}>
                      {/* LLM Detail */}
                      <Grid size={{ xs: 12, md: hasLlmUsage ? 7 : 12 }}>
                        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                            <Box component="span" sx={{ color: '#7c4dff', fontSize: 20 }}>●</Box>
                            LLM / AI Costs
                          </Typography>
                          {hasLlmUsage ? (
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Tokens</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Calls</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      <Tooltip title="Blended avg of input + output rate per 1M tokens">
                                        <span>Rate (per 1M)</span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {llm.breakdown.map((row, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={500}>{row.label}</Typography>
                                      </TableCell>
                                      <TableCell align="right">{row.tokens.toLocaleString()}</TableCell>
                                      <TableCell align="right">{row.calls || '—'}</TableCell>
                                      <TableCell align="right">${row.ratePerMillion.toFixed(2)}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        ${fmtCost(row.cost)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  <TableRow>
                                    <TableCell colSpan={4} sx={{ fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>
                                      Total LLM
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>
                                      ${fmtCost(llm.total)}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No AI/LLM calls were recorded for this execution.
                            </Typography>
                          )}
                        </Paper>
                      </Grid>

                      {/* Infrastructure Detail */}
                      <Grid size={{ xs: 12, md: hasLlmUsage ? 5 : 12 }}>
                        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                            <Box component="span" sx={{ color: '#00bfa5', fontSize: 20 }}>●</Box>
                            Infrastructure Costs
                          </Typography>

                          {/* EC2 */}
                          <Box sx={{ mb: 2.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              AWS EC2 — t3.medium (ap-southeast-1)
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">Hourly Rate</Typography>
                                <Typography variant="body2" fontWeight={500}>${ec2.hourlyRate}/hr</Typography>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">Execution Time</Typography>
                                <Typography variant="body2" fontWeight={500}>{formatDuration(durationMs)}</Typography>
                              </Grid>
                              {safeConurrent > 1 && (
                                <>
                                  <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary">Full EC2 Cost</Typography>
                                    <Typography variant="body2" fontWeight={500}>${fmtCost(ec2.grossCost)}</Typography>
                                  </Grid>
                                  <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary">÷ {safeConurrent} concurrent</Typography>
                                    <Typography variant="body2" fontWeight={500} color="text.secondary">= your share</Typography>
                                  </Grid>
                                </>
                              )}
                              <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary">Your Share</Typography>
                                <Typography variant="body2" fontWeight={700} color="success.main">
                                  ${fmtCost(ec2.total)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          {/* MongoDB Cluster */}
                          <Box sx={{ mb: 2.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                              <Box component="span" sx={{ color: '#ff6d00', fontSize: 14 }}>●</Box>
                              MongoDB Atlas — {mongo.tier} (Singapore)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {mongo.tierInfo.ram} · {mongo.tierInfo.vcpu} · {mongo.tierInfo.storage} storage
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">Hourly Rate</Typography>
                                <Typography variant="body2" fontWeight={500}>${mongo.hourlyRate}/hr</Typography>
                              </Grid>
                              {safeConurrent > 1 && (
                                <>
                                  <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary">Full Cluster Cost</Typography>
                                    <Typography variant="body2" fontWeight={500}>${fmtCost(mongo.grossCost)}</Typography>
                                  </Grid>
                                  <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary">÷ {safeConurrent} concurrent</Typography>
                                    <Typography variant="body2" fontWeight={500} color="text.secondary">= your share</Typography>
                                  </Grid>
                                </>
                              )}
                              <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary">Your Share</Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: '#ff6d00' }}>
                                  ${fmtCost(mongo.total)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* MongoDB Data Transfer */}
                          {mongo.transfer.total > 0 && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Box sx={{ mb: 2.5 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                                  <Box component="span" sx={{ color: '#ff8f00', fontSize: 14 }}>●</Box>
                                  MongoDB Data Transfer
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary">Steps</Typography>
                                    <Typography variant="body2" fontWeight={500}>{mongo.transfer.stepCount}</Typography>
                                  </Grid>
                                  <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="text.secondary">~Data Transferred</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {(mongo.transfer.gb * 1024).toFixed(2)} MB
                                    </Typography>
                                  </Grid>
                                  <Grid size={{ xs: 12 }}>
                                    <Typography variant="caption" color="text.secondary">Estimated Cost ($0.015/GB)</Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#ff8f00' }}>
                                      ${fmtCost(mongo.transfer.total)}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            </>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* AWS Textract */}
                    {textract.used && (
                      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                          <Box component="span" sx={{ color: '#d32f2f', fontSize: 20 }}>●</Box>
                          AWS Textract (OCR)
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Step</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>API Type</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Pages</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                  <Tooltip title="Per page rate for this Textract API in ap-southeast-1">
                                    <span>Rate/Page</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {textract.breakdown.map((row, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={500}>{row.stepName}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{row.label}</Typography>
                                    {row.estimated && (
                                      <Chip label="estimated" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }} />
                                    )}
                                  </TableCell>
                                  <TableCell align="right">{row.pages}</TableCell>
                                  <TableCell align="right">${row.perPage.toFixed(4)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    ${fmtCost(row.cost)}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell colSpan={2} sx={{ fontWeight: 700, borderTop: '2px solid #e0e0e0' }}>
                                  Total — {textract.pages} page{textract.pages !== 1 ? 's' : ''}
                                </TableCell>
                                <TableCell colSpan={2} sx={{ borderTop: '2px solid #e0e0e0' }} />
                                <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #e0e0e0', color: '#d32f2f' }}>
                                  ${fmtCost(textract.total)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )}

                    {/* AWS S3 + Bedrock + SQS row */}
                    <Grid container spacing={3} sx={{ mt: 0 }}>
                      {/* S3 */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                            <Box component="span" sx={{ color: '#0288d1', fontSize: 20 }}>●</Box>
                            AWS S3
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">Input Files</Typography>
                              <Typography variant="body2" fontWeight={500}>{s3.inputFiles}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">Output Files</Typography>
                              <Typography variant="body2" fontWeight={500}>{s3.outputFiles}</Typography>
                            </Grid>
                            {s3.s3VarsCount > 0 && (
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">S3 Variables</Typography>
                                <Typography variant="body2" fontWeight={500}>{s3.s3VarsCount}</Typography>
                              </Grid>
                            )}
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">~Total Data</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {formatFileSize(s3.totalBytes)}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Divider sx={{ my: 1 }} />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">Storage</Typography>
                              <Typography variant="body2">${fmtCost(s3.storageCost)}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">Requests</Typography>
                              <Typography variant="body2">${fmtCost(s3.requestCost)}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">Transfer Out</Typography>
                              <Typography variant="body2">${fmtCost(s3.transferCost)}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">Total S3</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ color: '#0288d1' }}>
                                ${fmtCost(s3.total)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>

                      {/* Bedrock Overhead */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                            <Box component="span" sx={{ color: '#e91e63', fontSize: 20 }}>●</Box>
                            AWS Bedrock Overhead
                          </Typography>
                          {bedrock.used ? (
                            <Grid container spacing={1}>
                              <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary">Bedrock Input Tokens</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {bedrock.inputTokens.toLocaleString()}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Overhead Rate ($0.0002 / 1K input tokens)
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: '#e91e63' }}>
                                  ${fmtCost(bedrock.total)}
                                </Typography>
                              </Grid>
                            </Grid>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No AWS Bedrock models were used in this execution.
                            </Typography>
                          )}
                        </Paper>
                      </Grid>

                      {/* SQS */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
                            <Box component="span" sx={{ color: '#f57c00', fontSize: 20 }}>●</Box>
                            AWS SQS (Textract Queue)
                          </Typography>
                          {sqs.used ? (
                            <Grid container spacing={1}>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">Textract Steps</Typography>
                                <Typography variant="body2" fontWeight={500}>{textract.breakdown.length}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">~SQS Messages</Typography>
                                <Typography variant="body2" fontWeight={500}>{sqs.messages}</Typography>
                              </Grid>
                              <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Cost ($0.40 / 1M requests)
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: '#f57c00' }}>
                                  ${fmtCost(sqs.total)}
                                  {sqs.total < 0.0001 && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                      (&lt; $0.0001)
                                    </Typography>
                                  )}
                                </Typography>
                              </Grid>
                            </Grid>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No Textract jobs queued (SQS not used).
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Disclaimer */}
                    <Alert severity="info" sx={{ mt: 3 }} icon={<InfoIcon />}>
                      <Typography variant="body2">
                        <strong>Disclaimer:</strong> This is a rough estimate using approximate AWS ap-southeast-1 on-demand pricing.
                        LLM costs use a blended average of input/output token rates.
                        Infrastructure costs are pro-rated to the execution duration; EC2 and MongoDB are shared across concurrent executions.
                        S3 storage is pro-rated to the execution duration relative to a 30-day month.
                        Textract page counts are derived from step performance metrics where available.
                        MongoDB data transfer is estimated from step count.
                        Actual costs vary based on reserved instances, data volume, and regional surcharges.
                      </Typography>
                    </Alert>
                  </>
                );
              })()}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Result Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Step Result Details
        </DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedResult.stepName}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedResult.output, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog
        open={filePreviewDialog}
        onClose={() => {
          // Clean up blob URL to prevent memory leaks
          if (selectedFile?.blob && selectedFile?.previewUrl) {
            URL.revokeObjectURL(selectedFile.previewUrl);
          }
          setFilePreviewDialog(false);
          setSelectedFile(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <AssignmentIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedFile?.originalName || selectedFile?.fileName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedFile && `${formatFileSize(selectedFile.fileSize || 0)} • ${selectedFile.fileType}`}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFile && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              {selectedFile.isImage ? (
                <Box>
                  {selectedFile.error ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Preview Error</Typography>
                      <Typography variant="body2">{selectedFile.error}</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleFileClick(selectedFile)}
                        sx={{ mt: 1 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  ) : selectedFile.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Loading image preview...
                      </Typography>
                    </Box>
                  ) : selectedFile.previewUrl ? (
                    <img
                      src={selectedFile.previewUrl}
                      alt={selectedFile.originalName || selectedFile.fileName}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                </Box>
              ) : selectedFile.isPdf ? (
                <Box>
                  {selectedFile.error ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Preview Error</Typography>
                      <Typography variant="body2">{selectedFile.error}</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleFileClick(selectedFile)}
                        sx={{ mt: 1 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  ) : selectedFile.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Loading PDF preview...
                      </Typography>
                    </Box>
                  ) : selectedFile.previewUrl ? (
                    <iframe
                      src={selectedFile.previewUrl}
                      title={selectedFile.originalName || selectedFile.fileName}
                      style={{
                        width: '100%',
                        height: '70vh',
                        border: 'none',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <Alert severity="error" sx={{ mt: 2, display: 'none' }}>
                    Unable to load PDF preview. The file may not be accessible or may have been deleted.
                  </Alert>
                </Box>
              ) : (
                <Alert severity="info">
                  <Typography variant="subtitle2">File Preview Not Available</Typography>
                  <Typography variant="body2">
                    Preview is only available for image files (PNG, JPG, GIF, etc.) and PDF files. 
                    This file type ({selectedFile.fileType}) cannot be previewed in the browser.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilePreviewDialog(false)}>
            Close
          </Button>
          {selectedFile && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => {
                // For input files, use filename; for output files, use fileId
                const fileId = selectedFile.filename || selectedFile.fileId;
                const fileName = selectedFile.fileName || selectedFile.originalName;
                
                handleDownloadFile(fileId, fileName);
              }}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default ProjectExecutionResults; 